import { useState, useEffect } from 'react';
import { Spinner } from '../ui/Icon.js';
import { Button } from '../ui/Button.js';
import { Dialog } from '../ui/Dialog.js';
import type { BrandDetail, BrandTag, Tag } from '../../types/index.js';
import { apiGet } from '../../lib/api.js';
import { apiPost, apiPatch, apiDelete } from '../../lib/api-mutations.js';
import { useToast } from '../../hooks/useToast.js';
import type { TagListResponse } from '../../types/index.js';
import { SUPPORTED_LOCALES } from '../../lib/locales.js';
import { BrandSharingSection } from './BrandSharingSection.js';
import { BrandRegionsSection } from './BrandRegionsSection.js';

const DOMAIN_RE =
  /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;

interface BrandFormProps {
  tenantId: string;
  brand: BrandDetail | null; // null = create mode
  onClose: () => void;
  onSaved: () => void;
  /**
   * Spec 129: optional callback fired when a sharing mutation (lock/unlock/
   * member add/remove) changes the brand without going through Save. Lets the
   * caller refresh its list + the global BrandContext while the modal stays
   * open. Defaults to a no-op.
   */
  onBrandChanged?: () => void;
}

export function BrandForm({
  tenantId,
  brand,
  onClose,
  onSaved,
  onBrandChanged,
}: BrandFormProps) {
  const { addToast } = useToast();
  const [name, setName] = useState(brand?.name ?? '');
  const [domain, setDomain] = useState(brand?.domain ?? '');
  const [industry, setIndustry] = useState(brand?.industry ?? '');
  const [locale, setLocale] = useState(brand?.locale ?? 'en-US');
  const [aliases, setAliases] = useState(brand?.aliases?.join(', ') ?? '');
  const [description, setDescription] = useState(brand?.description ?? '');
  // Phase 9 Session E.1 — optional sitemap URL. Empty string is normalised
  // to `null` on submit so the backend stores NULL and the resolver falls
  // back to `https://${domain}/sitemap.xml`.
  const [sitemapUrl, setSitemapUrl] = useState(brand?.sitemap_url ?? '');
  // Spec 151 Phase A — optional markdown tone-of-voice guide. Empty string is
  // normalised to `null` on submit so the backend clears the column (BE treats
  // both `''` and `null` as a clear).
  const [toneOfVoiceGuide, setToneOfVoiceGuide] = useState(
    brand?.tone_of_voice_guide ?? '',
  );
  const TONE_OF_VOICE_MAX = 50000;
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Tag management
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [assignedTagIds, setAssignedTagIds] = useState<Set<string>>(
    new Set(brand?.tags?.map((t) => t.id) ?? []),
  );
  const [tagsLoading, setTagsLoading] = useState(false);

  useEffect(() => {
    setTagsLoading(true);
    apiGet<TagListResponse>(`/api/v1/tenants/${tenantId}/tags`)
      .then((res) => setAllTags(res.data))
      .catch(() => {})
      .finally(() => setTagsLoading(false));
  }, [tenantId]);

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!name.trim()) e.name = 'Name is required';
    if (!domain.trim()) e.domain = 'Domain is required';
    else if (!DOMAIN_RE.test(domain.trim())) e.domain = 'Must be a valid hostname (e.g., example.com)';
    if (!industry.trim()) e.industry = 'Industry is required';
    const sitemapTrimmed = sitemapUrl.trim();
    if (sitemapTrimmed !== '') {
      try {
        const parsed = new URL(sitemapTrimmed);
        if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
          e.sitemap_url = 'Must be an http(s) URL';
        }
      } catch {
        e.sitemap_url = 'Must be a valid URL (including protocol)';
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      const sitemapTrimmed = sitemapUrl.trim();
      const body = {
        name: name.trim(),
        domain: domain.trim(),
        industry: industry.trim(),
        locale,
        aliases: aliases
          .split(',')
          .map((a) => a.trim())
          .filter(Boolean),
        description: description.trim() || undefined,
        // Empty string → null so the backend clears the column. Trimmed
        // string is sent verbatim — server validates http(s) URL.
        sitemap_url: sitemapTrimmed === '' ? null : sitemapTrimmed,
        // Spec 151 Phase A — optional markdown guide. Empty → null clears it
        // (BE treats `''` and `null` identically). Sent verbatim otherwise so
        // markdown whitespace/indentation is preserved.
        tone_of_voice_guide: toneOfVoiceGuide === '' ? null : toneOfVoiceGuide,
      };

      let savedBrandId: string;

      if (brand) {
        // Update
        const res = await apiPatch<{ data: { id: string } }>(
          `/api/v1/tenants/${tenantId}/brands/${brand.id}`,
          body,
        );
        savedBrandId = res.data.id;
        addToast('Brand updated successfully', 'success');
      } else {
        // Create
        const res = await apiPost<{ data: { id: string } }>(
          `/api/v1/tenants/${tenantId}/brands`,
          body,
        );
        savedBrandId = res.data.id;
        addToast('Brand created successfully', 'success');
      }

      // Sync tag assignments if editing
      if (brand) {
        const currentTags = new Set(brand.tags?.map((t) => t.id) ?? []);
        // Tags to add
        for (const tagId of assignedTagIds) {
          if (!currentTags.has(tagId)) {
            await apiPost(`/api/v1/tenants/${tenantId}/brands/${savedBrandId}/tags/${tagId}`, {});
          }
        }
        // Tags to remove
        for (const tagId of currentTags) {
          if (!assignedTagIds.has(tagId)) {
            await apiDelete(`/api/v1/tenants/${tenantId}/brands/${savedBrandId}/tags/${tagId}`);
          }
        }
      }

      onSaved();
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to save brand', 'error');
    } finally {
      setSaving(false);
    }
  }

  function toggleTag(tagId: string) {
    setAssignedTagIds((prev) => {
      const next = new Set(prev);
      if (next.has(tagId)) next.delete(tagId);
      else next.add(tagId);
      return next;
    });
  }

  return (
    <Dialog open onClose={onClose} title={brand ? 'Edit brand' : 'Add brand'}>
      <form onSubmit={handleSubmit} className="mt-4 flex w-[min(32rem,80vw)] flex-col gap-4">
        {/* Name */}
        <div className="hub-field">
          <label className="hub-field__label">
            Name <span className="text-[var(--hub-color-danger)]">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={`hub-input ${errors.name ? 'border-[var(--hub-color-danger)]' : ''}`}
            placeholder="Acme Corp"
          />
          {errors.name && <p className="hub-field__help text-[var(--hub-color-danger)]">{errors.name}</p>}
        </div>

        {/* Domain */}
        <div className="hub-field">
          <label className="hub-field__label">
            Domain <span className="text-[var(--hub-color-danger)]">*</span>
          </label>
          <input
            type="text"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            className={`hub-input ${errors.domain ? 'border-[var(--hub-color-danger)]' : ''}`}
            placeholder="acme.com"
          />
          {errors.domain && <p className="hub-field__help text-[var(--hub-color-danger)]">{errors.domain}</p>}
        </div>

        {/* Sitemap URL (Phase 9 Session E.1) */}
        <div className="hub-field">
          <label className="hub-field__label">
            Sitemap URL <span className="text-[var(--hub-color-ink-subtle)]">(optional)</span>
          </label>
          <input
            type="text"
            data-testid="brand-form-sitemap-url"
            value={sitemapUrl}
            onChange={(e) => setSitemapUrl(e.target.value)}
            className={`hub-input ${errors.sitemap_url ? 'border-[var(--hub-color-danger)]' : ''}`}
            placeholder="https://acme.com/sitemap.xml"
          />
          <p className="hub-field__help">
            {domain.trim()
              ? `Leave blank to use https://${domain.trim()}/sitemap.xml`
              : "Leave blank to use the default sitemap path on the brand's domain."}
          </p>
          {errors.sitemap_url && (
            <p className="hub-field__help text-[var(--hub-color-danger)]">{errors.sitemap_url}</p>
          )}
        </div>

        {/* Industry */}
        <div className="hub-field">
          <label className="hub-field__label">
            Industry <span className="text-[var(--hub-color-danger)]">*</span>
          </label>
          <input
            type="text"
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            className={`hub-input ${errors.industry ? 'border-[var(--hub-color-danger)]' : ''}`}
            placeholder="Technology"
          />
          {errors.industry && <p className="hub-field__help text-[var(--hub-color-danger)]">{errors.industry}</p>}
        </div>

        {/* Primary locale (Spec 078) */}
        <div className="hub-field">
          <label className="hub-field__label">Primary locale</label>
          <select
            value={locale}
            onChange={(e) => setLocale(e.target.value)}
            className="hub-select"
          >
            {SUPPORTED_LOCALES.map((loc) => (
              <option key={loc.value} value={loc.value}>
                {loc.label}
              </option>
            ))}
          </select>
          <p className="hub-field__help">
            Controls the language &amp; market used when discovering prompt suggestions.
          </p>
        </div>

        {/* Aliases */}
        <div className="hub-field">
          <label className="hub-field__label">
            Aliases <span className="text-[var(--hub-color-ink-subtle)]">(comma-separated)</span>
          </label>
          <input
            type="text"
            value={aliases}
            onChange={(e) => setAliases(e.target.value)}
            className="hub-input"
            placeholder="Acme, ACME Corp"
          />
        </div>

        {/* Description */}
        <div className="hub-field">
          <label className="hub-field__label">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="hub-input"
            placeholder="Brief description of the brand..."
          />
        </div>

        {/* Tone of voice guide (Spec 151 Phase A) — optional markdown
            source text used later when GEO Observatory suggests content changes. */}
        <div className="hub-field">
          <label htmlFor="brand-tone-of-voice-input" className="hub-field__label">
            Tone of voice guide <span className="text-[var(--hub-color-ink-subtle)]">(optional)</span>
          </label>
          <textarea
            id="brand-tone-of-voice-input"
            data-testid="brand-tone-of-voice-input"
            value={toneOfVoiceGuide}
            onChange={(e) => setToneOfVoiceGuide(e.target.value)}
            rows={9}
            maxLength={TONE_OF_VOICE_MAX}
            className="hub-input font-mono"
            placeholder={
              'e.g.\n# Voice\nConfident, warm, jargon-free.\n\n## Do\n- Short sentences\n\n## Avoid\n- Hype words'
            }
          />
          <div className="flex items-center justify-between">
            <p className="hub-field__help">Markdown supported.</p>
            <p
              className={`hub-field__help ${
                toneOfVoiceGuide.length > TONE_OF_VOICE_MAX * 0.9
                  ? 'text-[var(--hub-color-warning)]'
                  : ''
              }`}
            >
              {toneOfVoiceGuide.length.toLocaleString()} /{' '}
              {TONE_OF_VOICE_MAX.toLocaleString()}
            </p>
          </div>
        </div>

        {/* Tags (only show in edit mode to keep create flow simple) */}
        {brand && (
          <div className="hub-section">
            <div className="hub-section__head">
              <h2>Tags</h2>
            </div>
            {tagsLoading ? (
              <p className="text-sm text-[var(--hub-color-ink-subtle)]">Loading tags...</p>
            ) : allTags.length === 0 ? (
              <p className="text-sm text-[var(--hub-color-ink-subtle)]">
                No tags created yet. Create tags in the Tags tab.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {allTags.map((tag) => {
                  const selected = assignedTagIds.has(tag.id);
                  return (
                    <button
                      key={tag.id}
                      type="button"
                      onClick={() => toggleTag(tag.id)}
                      className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium transition-all ${
                        selected
                          ? 'text-white ring-2 ring-offset-1'
                          : 'bg-[var(--hub-color-surface-sunken)] text-[var(--hub-color-ink-muted)] hover:bg-[var(--hub-color-band)]'
                      }`}
                      style={
                        selected
                          ? { backgroundColor: tag.color, ['--tw-ring-color' as string]: tag.color } as React.CSSProperties
                          : undefined
                      }
                    >
                      {!selected && (
                        <span
                          className="inline-block h-2.5 w-2.5 rounded-full"
                          style={{ backgroundColor: tag.color }}
                        />
                      )}
                      {tag.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Monitored regions (Spec 191) — edit mode only (needs a brand id).
            Self-contained: mutates immediately and refreshes via onChange. */}
        {brand && (
          <BrandRegionsSection
            brand={brand}
            onChange={() => onBrandChanged?.()}
          />
        )}

        {/* Sharing (Spec 129) — edit mode only; ported from the retired
            BrandsPage SharingPanel. `brand` (BrandDetail) extends Brand so it
            satisfies BrandSharingSection's prop. */}
        {brand && (
          <BrandSharingSection
            brand={brand}
            onChange={() => onBrandChanged?.()}
          />
        )}

        {/* Actions */}
        <div className="hub-section flex justify-end gap-3">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={saving}>
            {saving && <Spinner />}
            {brand ? 'Save changes' : 'Create brand'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
